import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Mail, RefreshCw } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuthStore } from '../stores/authStore';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuthStore();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const hasVerified = useRef(false); // Track if we've already attempted verification

  const token = searchParams.get('token');

  useEffect(() => {
    // Only verify once, even if component re-renders
    if (token && !hasVerified.current && !isRedirecting) {
      hasVerified.current = true;
      verifyEmail(token);
    } else if (!token && !hasVerified.current && !isRedirecting) {
      setStatus('error');
      setMessage('Invalid verification link - no token provided');
    }
  }, [token]); // Remove status from dependencies to prevent re-verification

  const verifyEmail = async (verificationToken) => {
    try {
      const API_BASE = process.env.REACT_APP_API_URL || '';
      console.log('🔐 VerifyEmail: Starting verification for token:', verificationToken);
      
      const response = await fetch(`${API_BASE}/api/auth/verify-email?token=${verificationToken}`, {
        credentials: 'include'
      });

      const data = await response.json();
      
      console.log('🔐 VerifyEmail: Server response:', {
        ok: response.ok,
        status: response.status,
        data: data
      });

      if (response.ok && data.success && data.emailVerified) {
        console.log('✅ VerifyEmail: Email verification successful');
        setStatus('success');
        setMessage(data.message || 'Email verified successfully!');
        setIsRedirecting(true);
        
        // Redirect immediately to prevent any error flashing
        console.log('🔀 VerifyEmail: Refreshing auth state and redirecting...');
        
        // Refresh auth state to pick up the email verification
        await checkAuth();
        
        // Navigate to dashboard immediately
        navigate('/dashboard', { replace: true });
        return; // Exit early to prevent any further state updates
        
      } else {
        console.log('❌ VerifyEmail: Email verification failed:', data);
        setStatus('error');
        setMessage(data.error || 'Email verification failed');
      }
    } catch (error) {
      console.error('❌ VerifyEmail: Network error during verification:', error);
      setStatus('error');
      setMessage('Network error occurred. Please try again.');
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setMessage('Please enter your email address');
      return;
    }

    setResending(true);
    
    try {
      const API_BASE = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_BASE}/api/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage('Verification email sent! Please check your inbox.');
      } else {
        setMessage(data.error || 'Failed to send verification email');
      }
    } catch (error) {
      console.error('Resend verification error:', error);
      setMessage('Something went wrong. Please try again.');
    } finally {
      setResending(false);
    }
  };

  // If we're redirecting, show a minimal loading state to prevent error flashing
  if (isRedirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 text-center">
          <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full">
            <div className="bg-green-100">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Email Verified!</h2>
          <p className="mt-2 text-sm text-gray-600">Redirecting to dashboard...</p>
          <LoadingSpinner size="md" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full">
            {status === 'loading' && (
              <div className="bg-blue-100 rounded-full p-3">
                <Mail className="h-10 w-10 text-blue-600" />
              </div>
            )}
            {status === 'success' && (
              <div className="bg-green-100">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
            )}
            {status === 'error' && (
              <div className="bg-red-100">
                <XCircle className="h-10 w-10 text-red-600" />
              </div>
            )}
          </div>
          
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            {status === 'loading' && 'Verifying Your Email'}
            {status === 'success' && 'Email Verified!'}
            {status === 'error' && 'Verification Failed'}
          </h2>
          
          <p className="mt-2 text-sm text-gray-600">
            {message}
          </p>
          
          {/* Debug info */}
          {process.env.NODE_ENV === 'development' && (
            <p className="mt-1 text-xs text-gray-400">
              Status: {status} | Message: {message}
            </p>
          )}
        </div>

        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {status === 'success' && (
            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex">
                  <CheckCircle className="h-5 w-5 text-green-400 mt-0.5" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800">
                      Email Successfully Verified
                    </h3>
                    <p className="mt-2 text-sm text-green-700">
                      {message}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="text-center py-4">
                <LoadingSpinner size="md" />
                <p className="mt-4 text-gray-600">
                  {isRedirecting ? 'Redirecting to dashboard...' : 'Preparing your dashboard...'}
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  You'll be redirected automatically in a moment
                </p>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex">
                  <XCircle className="h-5 w-5 text-red-400 mt-0.5" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Verification Failed
                    </h3>
                    <p className="mt-2 text-sm text-red-700">
                      {message}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail className="inline w-4 h-4 mr-1" />
                  Enter your email to resend verification
                </label>
                <div className="flex space-x-2">
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="your.email@example.com"
                  />
                  <button
                    onClick={handleResendVerification}
                    disabled={resending}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {resending ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Resend
                      </>
                    )}
                  </button>
                </div>
              </div>

              <Link
                to="/"
                className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Back to Home
              </Link>
            </div>
          )}

          {status === 'loading' && (
            <div className="text-center py-8">
              <LoadingSpinner size="lg" />
              <p className="mt-4 text-gray-600">Verifying your email address...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
